import { useMemo, useState } from "react";

import { selectProjectsAcrossEnvironments, selectSidebarThreadsAcrossEnvironments } from "~/store";
import { useEnvironmentStoreSnapshot } from "~/hooks/useEnvironmentStoreSnapshot";
import type { NoActiveThreadCommandTaskRow } from "../NoActiveThreadState.logic";
import {
  buildUnifiedWorkItems,
  filterWorkItems,
  groupWorkItems,
  sortWorkItems,
  type HomebaseGroupBy,
  type HomebaseSortBy,
  type HomebaseView,
  type WorkItem,
  type WorkItemGroup,
} from "./unifiedWorkItems";

export interface UnifiedWorkItemsState {
  readonly view: HomebaseView;
  readonly setView: (view: HomebaseView) => void;
  readonly sortBy: HomebaseSortBy;
  readonly setSortBy: (sortBy: HomebaseSortBy) => void;
  readonly groupBy: HomebaseGroupBy;
  readonly setGroupBy: (groupBy: HomebaseGroupBy) => void;
  readonly groups: readonly WorkItemGroup[];
  readonly flatItems: readonly WorkItem[];
  readonly totalCount: number;
  readonly activeAgentCount: number;
  readonly needsAttentionCount: number;
}

export function useUnifiedWorkItems(
  taskRows: readonly NoActiveThreadCommandTaskRow[],
): UnifiedWorkItemsState {
  const [view, setView] = useState<HomebaseView>("my-work");
  const [sortBy, setSortBy] = useState<HomebaseSortBy>("priority");
  const [groupBy, setGroupBy] = useState<HomebaseGroupBy>("project");

  const storeSnapshot = useEnvironmentStoreSnapshot();
  const projects = useMemo(() => selectProjectsAcrossEnvironments(storeSnapshot), [storeSnapshot]);
  const threadSummaries = useMemo(
    () => selectSidebarThreadsAcrossEnvironments(storeSnapshot),
    [storeSnapshot],
  );

  const projectNameByRef = useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projects) {
      map.set(`${project.environmentId}:${project.id}`, project.name);
    }
    return map;
  }, [projects]);

  const allItems = useMemo(
    () => buildUnifiedWorkItems(taskRows, threadSummaries, projectNameByRef),
    [taskRows, threadSummaries, projectNameByRef],
  );

  const activeAgentCount = useMemo(
    () => allItems.filter((item) => item.agentStatus === "running").length,
    [allItems],
  );

  const needsAttentionCount = useMemo(
    () => allItems.filter((item) => item.needsAttention).length,
    [allItems],
  );

  const filteredItems = useMemo(() => filterWorkItems(allItems, view), [allItems, view]);
  const sortedItems = useMemo(() => sortWorkItems(filteredItems, sortBy), [filteredItems, sortBy]);
  const groups = useMemo(() => groupWorkItems(sortedItems, groupBy), [sortedItems, groupBy]);

  return {
    view,
    setView,
    sortBy,
    setSortBy,
    groupBy,
    setGroupBy,
    groups,
    flatItems: sortedItems,
    totalCount: allItems.length,
    activeAgentCount,
    needsAttentionCount,
  };
}
